// Inventory i18n namespace (Module 06 §4). Wired into apps/web's aggregated
// `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically.
//
// `inventoryEn` is authoritative; `InventoryDict` widens its values to `string`
// (src/i18n-shape.ts) so every locale stays parity-checked at COMPILE time (a
// missing/extra key fails tsc) while carrying real translations.
//
// ru IS TRANSLATED — inventory.ru.ts, including the floor PWA block below.
// fr, zh, zh-Hant and hi are machine-translated in inventory.<locale>.ts. The
// claim is declared in src/i18n-completeness.ts and enforced by
// tools/ci/i18n-parity.ts, which fails the build if a key here is added
// without a Russian value.
//
// ENTITY NOUNS ARE NOT IN HERE. "Stock unit", "Lot", "Location", "Transfer",
// "Cycle count", "Off-cut" and friends are TERMINOLOGY keys resolved through
// `useTerm()` (Module 02 §2.5): a KazKabel operator sees "Bobbin" where a
// generic factory sees "Stock unit", and hardcoding either would silently
// override the tenant's own vocabulary. Only screen chrome lives here.

import type { Translated } from "../../../i18n-shape.js";
import { inventoryRuDict } from "./inventory.ru.js";

export const inventoryEn = {
  nav: {
    aria: "Inventory sections",
    lookup: "Stock lookup",
    movements: "Movements",
    atp: "Availability",
    uomConversions: "Unit conversions",
    approvals: "Approvals",
  },

  common: {
    // The AREA-level loading label (UX-3): announced while the section nav's
    // permission feed settles and the area holds its reserving skeleton. The
    // per-screen `loading` keys below stay — they name what each screen is
    // fetching, which is a different and more useful sentence.
    loading: "Loading inventory…",
    retry: "Retry",
    requestId: "Request ID",
    close: "Close",
    cancel: "Cancel",
    confirm: "Confirm",
    remove: "Remove",
    // The column that holds a row's controls. Shared, because "Actions" is the
    // same word on every table in the area and a §3.3 `DataTable` column needs
    // a header even when its cells label themselves — the header is what the
    // phone card prints above the control.
    actions: "Actions",
    none: "None",
    noLocation: "Not located",
    remainingOfCapacity: "{{remaining}} of {{capacity}} {{uom}}",
    waitingOn: "Not available",
    waitingUnknown: "Not available",
    resolving: "Loading name…",
  },

  // Honest-degradation copy, by owning module. "Waiting on Module 10" is
  // engineering shorthand a planner cannot act on; each line names the
  // CAPABILITY that is missing and what its absence does to the number. The
  // module number stays in the control's title attribute for support.
  waiting: {
    "04": "Work-order figures not counted",
    "05": "No off-cut discount",
    "07": "No inspection results",
    "09": "Customers not named",
    "10": "Inbound POs not counted",
    "12": "Decided here",
  },

  filters: {
    facility: "Facility",
    allFacilities: "All facilities",
    pagination: "Pagination",
    prev: "Previous",
    next: "Next",
    // Rev-7 FilterBar words (design-system §3.17).
    button: "Filters ({{n}})",
    title: "Filters",
    done: "Done",
    remove: "Remove {{label}}",
    clearAll: "Clear all",
  },

  money: {
    unitCost: "Unit cost",
    withheld: "Hidden",
    withheldHint: "A cost was recorded, but your role cannot see money on this record.",
    unvalued: "No value recorded",
    columnWithheld: "Cost values on this page are hidden for your role.",
    columnUnvalued: "No cost was recorded for anything on this page.",
  },

  errors: {
    generic: "The request could not be completed. Try again.",
    MOVEMENT_ILLEGAL: "That movement is not allowed between those location kinds.",
    INSUFFICIENT_REMAINING: "There is less left on this unit than the request needs.",
    INSUFFICIENT_ATP: "Not enough available stock to cover that quantity.",
    UOM_NOT_CONVERTIBLE: "There is no conversion between that unit and the product's base unit.",
    NEGATIVE_STOCK_BLOCKED:
      "That would take on-hand below zero, which this location does not allow.",
    LOCATION_HIERARCHY_INVALID: "That parent would create an invalid location tree.",
    VIRTUAL_LOCATION_IMMUTABLE: "System locations cannot be edited.",
    LOCATION_CODE_TAKEN: "Another location in this facility already uses that code.",
    LOCATION_NOT_COUNTABLE: "That location cannot be counted.",
    ALLOW_NEGATIVE_PRODUCTION_ONLY: "Negative stock can only be allowed on production locations.",
    MEASURE_PRECISION_LOSS: "That quantity cannot be converted without losing precision.",
    RESERVATION_CONFLICT: "This stock already has an active hold.",
    RESERVATION_INVALID: "That demand type or quantity cannot be reserved.",
    LOT_NOT_RELEASED: "This lot has not passed quality release yet.",
    LOT_STATE_INVALID: "That quality transition is not allowed from the lot's current state.",
    TRANSFER_STATE_INVALID: "The transfer is no longer in a state that allows that action.",
    COUNT_STATE_INVALID: "The count is no longer in a state that allows that action.",
    MOVEMENT_ALREADY_REVERSED: "This movement has already been reversed.",
    REVERSAL_NOT_REVERSIBLE: "A reversal cannot itself be reversed.",
    ADJUSTMENT_APPROVAL_REQUIRED: "This adjustment needs approval before it can post.",
    ADJUSTMENT_REQUEST_STALE_STOCK: "The stock moved since this request was raised.",
    UNIT_NOT_AVAILABLE: "This unit's current state does not allow that action.",
    CUT_NO_PIECES: "Enter at least one piece to cut.",
    CUT_PIECE_NOT_POSITIVE: "Every piece must have a positive length.",
    CUT_SOURCE_NEGATIVE: "The source unit has nothing left to cut.",
    CUT_SERIAL_TAKEN: "That serial is already in use.",
    CUT_TOLERANCE_INVALID: "The configured cut tolerance is not valid.",
    CUT_FAULT_OUT_OF_RANGE: "The fault position is outside the piece.",
    RESOURCE_BUSY: "Someone else is changing this right now. Try again in a moment.",
    // The floor outbox can provoke these, and they were the ONLY codes whose
    // whole purpose is to explain a queue to an operator — yet they fell through
    // to the server's raw English sentence for a ru/kk speaker.
    IDEMPOTENCY_KEY_REUSE:
      "This was already sent with different details. Open the item and redo it.",
    IDEMPOTENCY_IN_PROGRESS: "The system is still finishing this. It will retry on its own.",
    IDEMPOTENCY_KEY_REQUIRED: "That request was rejected by the system. Do it again.",
    TRANSFER_VARIANCE_REASON_REQUIRED: "A line received short needs a reason.",
    CUT_EXCEEDS_REMAINING: "That cut is longer than what is left on this item.",
    PERMISSION_DENIED: "You do not have permission to do that.",
    // §4.12's own refusals (D-315). Each used to fall back to the server's
    // English title for a Russian reader.
    LOT_NOT_FOUND: "That batch could not be found.",
    LOCATION_NOT_FOUND: "That location could not be found, or it is in a plant you cannot see.",
    PRODUCT_NOT_FOUND: "That product could not be found.",
    WORK_ORDER_NOT_FOUND:
      "We couldn't find the work order for this adjustment. Untick it to record the adjustment alone, or reopen the form from the order.",
    ADJUSTMENT_REQUEST_STATE_INVALID: "This request has already been decided or withdrawn.",
    ADJUSTMENT_REQUEST_NOT_FOUND:
      "This request does not exist, or it is in a plant you cannot see.",
    // The label sheet's refusals (D-343).
    QR_LABEL_TEMPLATE_NOT_FOUND:
      "Your industry pack has no label layout for this. A group admin can add it with the industry pack update in Settings.",
    PACK_NOT_INSTALLED: "Labels come from the industry pack, and none is installed yet.",
    LABEL_PRINTER_UNAVAILABLE: "Label printing isn't set up in this workspace.",
    QR_PAYLOAD_MISSING: "These items have no scan code to print yet.",
    QR_KIT_SELECTION_EMPTY: "Choose at least one item to print a label for.",
  },

  // Closed vocabularies from §2 — status pills, movement types, reasons.
  vocab: {
    status: {
      empty: "Empty",
      filled: "Filled",
      in_use: "In use",
      in_transit: "In transit",
      quarantined: "Quarantined",
      consumed: "Consumed",
      shipped: "Shipped",
      retired: "Retired",
      active: "Active",
      fulfilled: "Fulfilled",
      released: "Released",
      expired: "Expired",
    },
    adjustmentRequest: {
      pending_approval: "Waiting for approval",
      posted: "Approved and posted",
      rejected: "Rejected",
      cancelled: "Cancelled",
    },
    qc: {
      pending: "Pending QC",
      released: "Released",
      quarantined: "Quarantined",
      rejected: "Rejected",
    },
    transfer: {
      draft: "Draft",
      dispatched: "Dispatched",
      partially_received: "Partly received",
      received: "Received",
      cancelled: "Cancelled",
    },
    count: {
      draft: "Draft",
      in_progress: "Counting",
      review: "In review",
      pending_approval: "Awaiting approval",
      posted: "Posted",
      cancelled: "Cancelled",
    },
    line: {
      pending: "Not counted",
      counted: "Counted",
      recount_requested: "Recount",
      accepted: "Accepted",
      rejected: "Rejected",
    },
    movement: {
      receipt: "Receipt",
      production_output: "Production output",
      issue: "Issue",
      transfer_out: "Transfer out",
      transfer_in: "Transfer in",
      adjustment: "Adjustment",
      count_adjustment: "Count adjustment",
      cut_out: "Cut out",
      cut_in: "Cut in",
      scrap: "Scrap",
      return_to_supplier: "Return to supplier",
      shipment: "Shipment",
      repack: "Move",
      reversal: "Reversal",
      // The fifteenth type (D-076/D-081, 04-B-4): unconsumed WO material back
      // to stock — its own physical event, never a reversal.
      material_return: "Material return",
    },
    sourceDoc: {
      purchase_order: "Purchase order",
      work_order: "Work order",
      sales_shipment: "Shipment",
      transfer: "Transfer",
      cycle_count: "Cycle count",
      cut: "Cut",
      waste_ticket: "Waste ticket",
      manual_adjustment: "Manual adjustment",
      opening_balance: "Opening balance",
    },
    reason: {
      damage: "Damage",
      count_variance: "Count variance",
      data_correction: "Data correction",
      loss: "Loss",
      found: "Found",
      qc_fail: "Quality failure",
      shrinkage: "Shrinkage",
      remeasure: "Re-measure",
      cut_tolerance: "Cut tolerance",
    },
    scan: {
      lookup: "Looked up",
      locate: "Located",
      count: "Counted",
      receive: "Received",
      issue: "Issued",
      cut: "Cut",
      transfer_dispatch: "Dispatched",
      transfer_receive: "Received",
      remeasure: "Re-measured",
    },
    relation: {
      consumed_into: "consumed into",
      cut_from: "cut from",
      reprocessed_into: "reprocessed into",
      repacked_into: "repacked into",
    },
    origin: {
      purchased: "Purchased",
      produced: "Produced",
      reprocessed: "Reprocessed",
      split: "Split",
      opening_balance: "Opening balance",
      adjustment: "Adjustment",
    },
    unitKind: {
      bobbin: "Bobbin",
      drum: "Drum",
      barrel: "Barrel",
      coil: "Coil",
      roll: "Roll",
      container: "Container",
      pallet: "Pallet",
      box: "Box",
      piece: "Piece",
    },
    locationKind: {
      warehouse: "Warehouse",
      zone: "Zone",
      bin: "Bin",
      receiving: "Receiving",
      shipping: "Shipping",
      in_transit: "In transit",
      quarantine: "Quarantine",
      scrap: "Scrap",
      production: "Production",
      customer: "Customer",
      supplier: "Supplier",
    },
  },

  // §4.1 ---------------------------------------------------------------------
  lookup: {
    adjust: "Adjust stock",
    adjustRow: "Adjust",
    title: "Stock lookup",
    searchLabel: "Search stock",
    placeholder: "e.g. 70mm 200m, or a serial",
    hotkeyHint: "Press / to search",
    includeReserved: "Include reserved stock",
    idleTitle: "Search for stock",
    // The serial is named because it is the query a storekeeper standing at the
    // rack actually has (D-273). A capability nobody is told about is one
    // nobody uses, and this one existed for a day before anyone could find it.
    idleBody:
      "Type a size and a length — for example 70mm 200m — to find every matching item, or the serial printed on the drum in your hands.",
    loading: "Searching stock…",
    errorTitle: "Could not search stock",
    emptyTitle: "No stock matches “{{query}}”",
    emptyBody: "Try a different size, length, facility — or the serial on the label.",
    emptyChecking: "Checking for close alternatives…",
    emptyNearest: "Nearest: {{n}} items of {{product}} under {{measure}} {{uom}}.",
    emptyNoNearest: "There is nothing shorter to offer either.",
    emptyReservedTitle: "No available stock matches \u201c{{query}}\u201d",
    emptyReservedBody:
      "Every match is reserved for an order \u2014 {{count}} in total. Show them to see which.",
    showReserved: "Show reserved stock",
    showAnyway: "Show anyway",
    keyboardHint:
      "↑ ↓ to move through results · Enter to open · Shift+Enter to reserve · Esc to clear",
    // The server states truncation (§3.8 `truncated`); the copy no longer
    // spells a row count, because the count was a client-side mirror of a
    // server constant and drifting it would have made the sentence a lie.
    groupCapped: "This group is capped — more matches exist. Narrow the search to see them.",
    candidatesCapped:
      "More off-cut pieces exist than were compared, so the eligible count below is a partial one.",
    productsCapped:
      "Your search matches more products than can be searched at once — some stock is not shown. Add the SKU or a name to narrow it.",
    reservedFor: "Reserved · {{demand}} {{id}}",
    reserveBoth: "Reserve all {{n}} pieces",
    resolving: "Loading piece details…",
    hideAlternatives: "Hide alternatives",
    groups: {
      units: "{{unit}}",
      combos: "{{offcut}} combinations",
      bulk: "Bulk {{lot}}",
      nearest: "Under {{measure}} {{uom}}",
    },
    term: {
      offcut_discount: "Off-cut discount",
    },
    degradedIntro: "Some details are missing:",
    reserved: "On hold",
    reserve: "Reserve",
    cut: "Cut",
    open: "Open",
    neverScanned: "Never scanned",
    lastSeenMinutes: "Last seen {{n}} min ago",
    lastSeenHours: "Last seen {{n}}h ago",
    lastSeenDays: "Last seen {{n}} days ago",
    noCombos: "No pair of off-cuts covers this length.",
    // THE SAME EMPTY GROUP, SAID HONESTLY WHEN THE SEARCH WAS CAPPED. The
    // server reports `truncated.offcut_candidates` when its candidate window
    // filled, and a capped search that found nothing has NOT established that
    // nothing exists — it established that it stopped looking. Saying "no pair
    // covers this length" over a truncated scan is absence of evidence
    // rendered as evidence of absence, and a planner acts on it by cutting a
    // full drum. Caught on deployed staging 2026-08-17 (M06-S02): the
    // storekeeper saw 5 combos over 300 candidates, and the planner — whose
    // wider facility scope filled the 500-row window — saw the flat negative.
    noCombosCapped:
      "Too many off-cuts to compare them all, and no pair was found. Narrow by facility or check the pieces directly.",
    comboTotal: "{{total}} combined · {{surplus}} left over",
    discountLabel: "Discount:",
    rejected: {
      status_unavailable: "{{n}} nearby pieces are not available",
      lot_not_released: "{{n}} nearby pieces are awaiting quality release",
      reserved: "{{n}} nearby pieces are on hold",
      other_facility: "{{n}} nearby pieces are at another facility",
      non_positive_remaining: "{{n}} nearby pieces are empty",
    },
    colLot: "Lot",
    colProduct: "Product",
    colOnHand: "On hand",
    colLocation: "Location",
  },

  // §4.2 ---------------------------------------------------------------------
  unitCard: {
    loading: "Loading item…",
    errorTitle: "Could not load this item",
    notFoundTitle: "Item not found",
    notFoundBody:
      "This label is not known here. Check the code, or register it when the goods are received.",
    product: "Product",
    bareContainer: "Empty container (no product)",
    openLot: "Open lot",
    claimedVsMeasured: "Claimed vs measured",
    claimedMeasured: "claimed {{claimed}} · measured {{measured}} {{uom}}",
    driftBadge: "Drift {{pct}}",
    driftOwner: "See calibration",
    releaseHold: "Release hold",
    releaseHoldConfirm:
      "Release the hold for {{demand}} {{id}}? The stock returns to availability.",
    holdReleased: "Hold released.",
    releaseHoldFailed: "Could not release this hold.",
    reservationsNoPermission: "Your role cannot see the holds on this item.",
    childrenTruncated:
      "This item has more cut children than fit on one page — some may be missing.",
    lotEdges: "Lot-level links",
    edgeDownstream: "downstream",
    edgeUpstream: "upstream",
    noClaim: "No claimed value was recorded",
    noCapacity: "No capacity recorded",
    ofCapacity: "of {{capacity}} {{uom}} capacity",
    lastScan: "Last scan",
    lastScanValue: "{{action}} · {{when}}",
    activeHold: "Active hold",
    heldFor: "{{quantity}} {{uom}} held for {{demand}}",
    actions: "Item actions",
    actionCut: "Cut",
    actionRemeasure: "Re-measure",
    actionMove: "Move",
    actionReserve: "Reserve",
    actionQuarantine: "Quarantine",
    actionRelease: "Release",
    actionRetire: "Retire",
    // Rev-7: why an action is disabled, one line under the bar.
    blocked: {
      terminal: "This item is {{status}}. Nothing more can be done with it.",
      inTransit: "This item is on a transfer. Receive it first.",
      empty: "This item is empty.",
      quarantined: "Release the quarantine first.",
      notHeld: "This item is not in quarantine.",
    },
    tabsAria: "Item details",
    tabs: {
      history: "History",
      genealogy: "Genealogy",
      // "Reservations", not "Holds" (2026-08-20, task #112). Spec 07 §2.7's
      // three-words rule says a factory user must meet three DISTINCT words for
      // the three unrelated things this platform calls a hold — module 04's
      // flag on an operation, module 07's quarantine record, and module 06's
      // reservations. Russian already keeps them apart («блокировка операции»,
      // «карантин», «резерв»); English did not, because this tab said "Holds"
      // beside a quality hold on the very next screen. The Russian's own note
      // argued "hold" was plainer for an operator — but plainness that collides
      // with two other meanings is not plain, it is ambiguous, and this label
      // sits one click from both of them.
      reservations: "Reservations",
    },
    historyLoading: "Loading history…",
    historyError: "Could not load history",
    historyEmptyTitle: "No history yet",
    historyEmptyBody: "Movements and scans for this item will appear here.",
    colWhen: "When",
    colEvent: "Event",
    colQuantity: "Quantity",
    colNote: "Note",
    colDemand: "Demand",
    colStatus: "Status",
    colCreated: "Created",
    scanBy: "Scanned by {{who}}",
    genealogyLoading: "Loading genealogy…",
    genealogyError: "Could not load genealogy",
    genealogyNoPermission: "Your role does not have access to genealogy tracing.",
    genealogyNoLotTitle: "No lot",
    genealogyNoLotBody: "Genealogy is tracked per lot; this item does not belong to one yet.",
    genealogyEmptyTitle: "No linked items",
    genealogyEmptyBody: "Nothing has been recorded as a parent or child of this item.",
    parents: "Made from",
    children: "Used in",
    noneRecorded: "Nothing recorded",
    openLotTrace: "Open the full lot trace",
    reservationsLoading: "Loading holds…",
    reservationsError: "Could not load holds",
    reservationsEmptyTitle: "No holds",
    reservationsEmptyBody: "Nothing is reserved against this item.",
    reservationsBareTitle: "No product",
    reservationsBareBody: "An empty container cannot be reserved.",
    reservationsTruncated: "Only the most recent holds for this product were checked.",
  },

  // The shared location picker (components/location-picker.tsx) — one server
  // search behind every screen that asks a human to name a bin.
  locationPicker: {
    placeholder: "Type a code, name or path…",
    typeToSearch: "Start typing to find a location.",
    searching: "Searching…",
    noMatch: "No location matches “{{q}}”.",
    more: "More than {{n}} match — keep typing to narrow it down.",
    change: "Change",
    resolving: "Loading…",
    failed: "Could not search locations. Try again.",
  },

  // §4.3 ---------------------------------------------------------------------
  locations: {
    childCount: "{{count}} inside",
    collapseAria: "Hide what is inside {{code}}",
    expandAria: "Show what is inside {{code}} ({{count}})",
    adjustHere: "Adjust stock here",
    adjustRow: "Adjust",
    loading: "Loading locations…",
    errorTitle: "Could not load locations",
    emptyTitle: "No locations yet",
    emptyBody: "Run the onboarding print kit, or add a warehouse to start the tree.",
    treeAria: "Location tree",
    matchesAria: "Matching locations",
    searchLabel: "Search locations",
    searchPlaceholder: "Code, name or path — e.g. WH1/A",
    // `{{count}}` and a `_one` form: the commonest result of a code search is
    // exactly one, and "1 locations match." is the tell that nobody read it.
    matches: "{{count}} locations match.",
    matches_one: "{{count}} location matches.",
    matchesTruncated: "More than {{n}} locations match — narrow the search.",
    noMatchesTitle: "Nothing matches “{{q}}”",
    noMatchesBody: "Search matches a location's code, its name, or its path in the tree.",
    clearSearch: "Clear the search",
    noPhysical: "No warehouses, zones or bins yet.",
    virtualGroup: "System locations ({{n}})",
    virtualReadOnly: "System locations are created and maintained by the platform.",
    selectPrompt: "Select a location to see its details and contents.",
    pickAria: "Select {{code}} for label printing",
    rename: "Rename",
    renamed: "Renamed.",
    truncated: "Showing {{n}} locations — there are more.",
    showingAll: "Showing all {{n}} locations.",
    loadMore: "Load more locations",
    loadingMore: "Loading more…",
    orphans: "{{n}} locations whose parent is not on this page — shown flat, not as roots.",
    moveConfirm: "Move {{source}} and its {{count}} descendants under {{target}}?",
    moveConfirm_one: "Move {{source}} and its {{count}} descendant under {{target}}?",
    // When the source is on no loaded page its subtree is genuinely unknown
    // here, so the count is left unsaid rather than guessed at zero.
    moveConfirmOffPage: "Move {{source}}, and everything under it, under {{target}}?",
    moved: "{{source}} moved under {{target}}.",
    inactive: "Inactive",
    kind: "Kind",
    path: "Path",
    countable: "Counted",
    countableHint: "Include this location in cycle counts",
    active: "Active",
    activeHint: "Available for new movements",
    allowNegative: "Allow negative",
    allowNegativeHint: "Permit on-hand to dip below zero mid-shift",
    qrPayload: "QR payload",
    contents: "What is here",
    contentsLoading: "Loading contents…",
    contentsError: "Could not load contents",
    contentsEmptyTitle: "Nothing stored here",
    contentsEmptyBody: "This location currently holds no stock.",
    colProduct: "Product",
    colOnHand: "On hand",
    printQr: "Print QR labels ({{n}})",
    printQrBusy: "Rendering…",
    qrPickFirst: "Tick the locations you want stickers for.",
    qrPrinted: "{{n}} labels rendered. The PDF is downloading.",
    qrNoLink:
      "The sheet rendered, but the download link could not be minted just now. Open it from the QR kit list.",
    add: "Add a location",
    added: "Location created.",
    addSubmit: "Create location",
    addFailed: "Could not create that location.",
    code: "Code",
    name: "Name",
    parent: "Inside",
    pickParent: "Choose a parent…",
    pickFacility: "Choose a facility…",
    // Task #253: a bin goes inside a zone or straight inside a warehouse.
    parentRequired: "Choose where this location goes before creating it.",
    noBinParent: "This facility has no warehouse or zone yet. Create a warehouse first.",
    noZoneParent: "This facility has no warehouse yet. Create a warehouse first.",
    reparentFailed: "Could not move that location. Try again.",
    updateFailed: "Could not save that change. Try again.",
  },

  // §4.4 ---------------------------------------------------------------------
  transfers: {
    status: "Status",
    allStatuses: "All statuses",
    from: "From",
    to: "To",
    loading: "Loading transfers…",
    errorTitle: "Could not load transfers",
    emptyTitle: "No transfers",
    emptyBody: "Nothing matches these filters.",
    colCode: "Code",
    colRoute: "Route",
    // A transfer's other end in a plant this reader cannot see.
    otherPlant: "Another plant",
    colStatus: "Status",
    colEta: "ETA",
    colAge: "In transit",
    noEta: "No ETA",
    onTime: "On time",
    daysLate: "{{n}} days past ETA",
    new: "New transfer",
    emptyFilteredTitle: "No matching transfers",
    emptyFilteredBody: "Nothing matches these filters.",
    pickFacility: "Choose a facility",
    addLine: "Add a line",
    lineSerial: "Item serial (optional)",
    lineProduct: "Product (bulk line)",
    linePickProduct: "Search by code or name",
    lineProductFromUnit: "Taken from the scanned item.",
    lineLot: "Batch",
    lineLotAny: "No batch (unbatched stock)",
    lineLotTruncated: "Showing the {{n}} newest batches. Older batches are not listed here.",
    lineUom: "Unit",
    needFacilities: "Choose where the goods leave from and where they go.",
    needDistinctFacilities: "A transfer moves goods between two different sites.",
    needLine: "Add at least one line with a quantity and a unit.",
    needLineSubject: "Every line needs an item serial or a product.",
    createSubmit: "Create draft",
    creating: "Creating…",
    createFailed: "Could not create this transfer.",
    created: "Draft {{code}} created.",
    openCreated: "Open the draft",
  },
  transferDetail: {
    cancelDone: "Transfer cancelled.",
    noLinesTitle: "No lines yet",
    noLinesBody: "This draft has nothing on it — add lines before dispatching.",
    loading: "Loading transfer…",
    errorTitle: "Could not load this transfer",
    timeline: "Transfer progress",
    created: "Created",
    dispatched: "Dispatched",
    received: "Received",
    cancelled: "Cancelled",
    pending: "Not yet",
    by: "by {{who}}",
    carrier: "Carrier",
    cancelReason: "Reason",
    actions: "Transfer actions",
    dispatch: "Dispatch",
    dispatching: "Dispatching…",
    dispatchFailed: "Could not dispatch this transfer.",
    cancel: "Cancel transfer",
    confirmCancel: "Cancel this transfer",
    cancelFailed: "Could not cancel this transfer.",
    lines: "Lines",
    colProduct: "Product",
    colQty: "Sent",
    colReceived: "Received",
    colVariance: "Variance reason",
    receiveOnFloor: "Receiving happens on the floor app, where each item is scanned in.",
    activity: "Ledger activity",
    activityLoading: "Loading activity…",
    activityError: "Could not load activity",
    activityEmpty: "No ledger entries for this transfer yet.",
  },

  // §4.5 ---------------------------------------------------------------------
  counts: {
    status: "Status",
    allStatuses: "All statuses",
    loading: "Loading counts…",
    errorTitle: "Could not load counts",
    emptyTitle: "Nothing to review",
    emptyBody: "No counts match these filters.",
    // The list is reached by counters as well as reviewers (F1/F15), so the
    // empty state and the role note are said in the CALLER's terms — "nothing
    // to review" is a reviewer's sentence and was being shown to a storekeeper
    // who had just been told they cannot review.
    emptyListTitle: "No counts",
    emptyPerformBody: "Start one to count a location.",
    noReviewGrant: "Your role can view counts but not review or approve them.",
    performOnlyGrant: "Your role can start and perform counts; approving them is a reviewer's job.",
    colCode: "Count",
    colStatus: "Status",
    colBlind: "Mode",
    colSubmitted: "Submitted",
    colVarianceValue: "Variance value",
    blind: "Blind",
    open: "Open",
    new: "New count",
    noCountable: "No countable location in this facility yet.",
    blindToggle: "Blind count",
    blindHint: "A blind count hides expected quantities from the counter until review.",
    createSubmit: "Create count",
    createFailed: "Could not create this count.",
    created: "Count {{code}} created.",
    openCreated: "Open the count",
    worksheetOnFloor: "Counting happens on the floor app; this screen reviews the result.",
  },
  countReview: {
    loading: "Loading count…",
    errorTitle: "Could not load this count",
    totals: "Totals",
    totalsValue: "{{lines}} lines · {{variances}} to review",
    expectedHidden: "Expected quantities are hidden while this blind count is in progress.",
    emptyTitle: "No variances",
    emptyBody: "100% accurate count. Approve to post and close it.",
    colProduct: "Product",
    colExpected: "Expected",
    colCounted: "Counted",
    colDeltaQty: "Δ quantity",
    colDeltaValue: "Δ value",
    colStatus: "Status",
    colActions: "Actions",
    notCounted: "Skipped",
    accept: "Accept",
    reject: "Reject",
    recount: "Recount",
    rejectNote: "Why is this rejected?",
    confirmReject: "Reject line",
    reviewFailed: "Could not record that decision.",
    approve: "Approve and post",
    approving: "Approving…",
    approveFailed: "Could not approve this count.",
    posted: "Approved — adjustments posted to the ledger.",
    escalated: "Above your approval limit — sent for approval.",
    movedLines:
      "{{n}} lines moved since the count snapshot and were returned for recount. Nothing was posted.",
    notPosted: "Not posted.",
    localTier: "Decided here.",
    chain: "Approval chain {{id}}.",
    noLinesTitle: "No lines on this count",
    noLinesBody:
      "Nothing was scoped into it — cancel it, or start a new count over a location that holds stock.",
    blindPendingTitle: "Variances cannot be shown yet",
    blindPendingBody:
      "This blind count is still in progress and expected quantities are hidden from your role, so no variance can be calculated here.",
    colCountedBy: "Counted by",
    colInvestigate: "Since the count",
    openLedger: "Movements",
    recountOf: "Recount",
    approveConfirm:
      "Post {{n}} adjustments to the ledger? They are permanent, and undoing them means reversing each one.",
    approveConfirmSubmit: "Yes, post them",
    approveBlindValue:
      "You cannot see the value of these adjustments — you are approving quantities only.",
    approveUnvalued: "No cost is recorded for this stock, so the adjustments post unvalued.",
    pendingApprovalNote: "This count is above your approval limit and is waiting on a higher tier.",
    openApprovals: "Open approvals",
    cancelCount: "Cancel this count",
    cancelReason: "Why is it being cancelled?",
    cancelSubmit: "Cancel count",
    cancelFailed: "Could not cancel this count.",
  },

  // §4.6 ---------------------------------------------------------------------
  lotDetail: {
    adjust: "Adjust stock",
    adjustRow: "Adjust",
    loading: "Loading lot…",
    errorTitle: "Could not load this lot",
    product: "Product",
    origin: "Origin",
    supplier: "Supplier {{id}}",
    received: "Received",
    expires: "Expires",
    actions: "Lot actions",
    quarantine: "Quarantine",
    release: "Release",
    quarantineReason: "Why is this lot being held?",
    releaseNote: "Release note",
    qcFailed: "Could not change the quality status.",
    qcDone: "Quality status updated.",
    traceUp: "Trace ↑ upstream",
    traceDown: "Trace ↓ downstream",
    attributes: "Attributes",
    noAttributes: "No attributes recorded for this lot.",
    stockSummary: "Where it is",
    noStockTitle: "No stock on hand",
    noStockBody: "This lot has been fully consumed, shipped or scrapped.",
    colOnHand: "On hand",
    noUnits: "This lot is tracked in bulk — no individual items.",
    unitsTruncated: "Showing {{shown}} of {{total}} items.",
    colSerial: "Serial",
    colStatus: "Status",
    colRemaining: "Remaining",
  },

  // §4.7 ---------------------------------------------------------------------
  trace: {
    title: "Recall trace",
    loading: "Tracing…",
    errorTitle: "Could not run the trace",
    noPermissionTitle: "Tracing not available",
    noPermissionBody: "Your role does not have access to genealogy tracing.",
    direction: "Trace direction",
    up: "↑ Upstream",
    down: "↓ Downstream",
    exportPack: "Export recall pack",
    truncated: "This trace hit the depth limit — deeper links exist and are not shown.",
    truncatedPlain: "This trace hit the depth limit; deeper links exist and are not included.",
    clientTruncated:
      "This view stopped after {{n}} rows — the graph is larger than what is shown here.",
    clientTruncatedPlain:
      "This view stopped at its row limit; the graph is larger than what is included here.",
    depth: "depth {{n}}",
    tileFinishedLots: "Finished lots",
    tileStockUnits: "Items",
    tileShipments: "Shipments",
    tileCustomers: "Customers",
    tileRawLots: "Raw lots",
    tileSuppliers: "Suppliers",
    customersUnavailable: "Customers can't be named here. Shipments are listed in full.",
    customersUnavailablePlain: "Affected customers couldn't be named. Shipment rows are complete.",
    // `customer_resolution: partial` (D-286 ruling 24): some shipments on the
    // trace have no sales shipment on record — seeded stock, or stock shipped
    // before sales shipments existed — so the list is true and incomplete.
    customersPartial:
      "{{unresolved}} of {{total}} shipments have no sales shipment on record, so their customers are not known. Treat this customer list as incomplete.",
    customersPartialPlain:
      "Customer list incomplete: {{unresolved}} of {{total}} shipments have no sales shipment on record, so their customers are not known. Shipment rows are complete.",
    tileIncomplete: "incomplete",
    graph: "Chain",
    emptyTitle: "Nothing linked",
    emptyDownBody: "Nothing has consumed or shipped this lot yet.",
    emptyUpBody: "No parent lots or items were recorded for this lot.",
    alreadyShown: "(shown above)",
    boundary: "Where the trace stopped",
    boundaryHint: "These nodes were reached at the limit — expand them from their own page.",
    shipments: "Shipments",
    noShipments: "No shipments touch this lot.",
    colWhen: "When",
    colQty: "Quantity",
    colRef: "Reference",
  },

  // §4.8 ---------------------------------------------------------------------
  movements: {
    // §4.12: a posted request links to the one movement it wrote.
    sourceDocFilter: "Only the movement written by request {{id}}",
    title: "Movements",
    export: "Export CSV",
    exportPage: "Export these {{n}} rows",
    exportScopePlain: "Export of the {{n}} rows on screen — not the whole filtered set",
    // D-294 ruling 7: the file says how it is punctuated, because the file is
    // what gets forwarded.
    exportPunctuationCommaDot: "comma-separated, decimal point",
    exportPunctuationSemicolonComma: "semicolon-separated, decimal comma",
    serialPlaceholder: "Type an item serial",
    clearFilter: "Clear",
    allLocations: "All locations",
    type: "Type",
    allTypes: "All types",
    sourceDoc: "Source document",
    allSourceDocs: "All documents",
    product: "Product",
    productPlaceholder: "Search products…",
    idPlaceholder: "Paste an id",
    performer: "Performed by",
    performerPlaceholder: "User or agent id",
    from: "From",
    to: "To",
    loading: "Loading movements…",
    errorTitle: "Could not load movements",
    emptyTitle: "No movements",
    emptyBody: "Nothing matches these filters.",
    colWhen: "When",
    colType: "Type",
    colProduct: "Product",
    colQuantity: "Quantity",
    colSource: "Source",
    colExpand: "Detail",
    colFrom: "From location",
    colTo: "To location",
    colQtyBase: "Base quantity",
    colPerformer: "Performed by",
    colReason: "Reason",
    colNote: "Note",
    colUnit: "Item",
    colLot: "Lot",
    expand: "Open",
    collapse: "Close",
    reverse: "Reverse this movement",
    reverseWarning: "A reversal writes a new, permanent ledger entry. It cannot be undone.",
    reverseNote: "Note (optional)",
    reverseConfirm: "Yes, reverse it",
    reversing: "Reversing…",
    reversed: "Reversed. A balancing entry has been written.",
    reverseFailed: "Could not reverse this movement.",
  },

  // §4.9 ---------------------------------------------------------------------
  atp: {
    title: "Availability",
    product: "Product",
    productPlaceholder: "Search products…",
    pickFacility: "Choose a facility…",
    date: "As of",
    idleTitle: "Choose a product",
    idleBody: "Pick a product and facility to see how its available quantity is made up.",
    loading: "Calculating availability…",
    errorTitle: "Could not calculate availability",
    asOf: "{{product}} · {{date}}",
    partialSum: "Some terms are missing, so this total covers only the ones below.",
    colTerm: "Term",
    colValue: "Quantity",
    colDrill: "Detail",
    noDrill: "—",
    drillLedger: "See the ledger",
    drillReservedStock: "See this product's stock",
    drillTransfers: "See transfers in flight",
    term: {
      on_hand: "On hand",
      reserved: "Reserved",
      in_transit_inbound: "In transit inbound",
      po_inbound: "Purchase orders inbound",
      wo_planned_output: "Planned production",
      wo_demand: "Production demand",
    },
  },

  // §4.10 --------------------------------------------------------------------
  uom: {
    title: "Unit conversions",
    ownedByCatalogue:
      "Conversion factors are set on the product itself, in the catalogue. This page shows where one is missing.",
    scanning: "Checking recent movements…",
    window: "Checked against the {{n}} most recent movements.",
    errorTitle: "Could not check conversions",
    cleanTitle: "No missing conversions",
    cleanBody: "Every recent movement's unit converts to its product's base unit.",
    warning: "{{n}} products have movements in a unit with no conversion to their base unit.",
    colProduct: "Product",
    colBase: "Base unit",
    colMissing: "Missing conversion from",
    colFix: "Fix",
    openConversions: "Open conversions",
    viewConversions: "View conversions",
  },

  // §4.11 --------------------------------------------------------------------
  // The adjustment-approvals inbox: over-threshold (or unvalued) adjustments
  // that §3.9 HELD instead of posting. Nothing here has touched the ledger yet,
  // so every confirmation says what does and does not get written.
  approvals: {
    newAdjustment: "New adjustment",
    openRequest: "Open",
    pageRange: "Showing {{from}}–{{to}}",
    title: "Adjustment approvals",
    localTierNote: "How much you can approve depends on your role and the adjustment's value.",
    status: "Status",
    allStatuses: "All statuses",
    loading: "Loading approvals…",
    errorTitle: "Could not load approvals",
    emptyPendingTitle: "Nothing waiting for approval",
    emptyPendingBody:
      "Every request has been decided. Adjustments above the value threshold arrive here for approval before they post.",
    emptyTitle: "No requests",
    emptyBody: "Nothing matches these filters.",
    colRequested: "Requested",
    colBy: "Requested by",
    colWhat: "Adjustment",
    colValue: "Estimated value",
    colStatus: "Status",
    postedMovement: "See the posted movement",
    // Free-text on a human rejection (rendered as itself via defaultValue);
    // `stale_stock` is the one machine-written reason §3.9 can set.
    rejectReason: {
      stale_stock: "Rejected automatically — the stock moved after the request was raised.",
    },
    payloadUnreadable: "The held movement could not be read.",
    approveWarning:
      "Approve this adjustment? It posts to the ledger immediately, and undoing it means reversing the movement.",
    rejectWarning:
      "Reject this adjustment? Nothing is posted, and the requester has to raise it again.",
    cancelWarning: "Cancel this request? Nothing is posted, and it closes as cancelled.",
    note: "Note (optional)",
    noteRequired: "Why is this being rejected?",
    approve: "Approve",
    reject: "Reject",
    cancel: "Cancel request",
    readOnly: "Your role can see these requests but not decide them.",
    failed: "Could not record that decision.",
    approved: "Approved — posted to the ledger as movement {{id}}.",
    rejected: "Rejected. Nothing was posted.",
    cancelled: "Request cancelled. Nothing was posted.",
  },

  // Shared write panels ------------------------------------------------------
  reserve: {
    title: "Reserve stock",
    // The panel places ONE kind of hold and says which (D-151). The words are
    // deliberately about the situation ("someone asked for it") rather than the
    // schema — `quote_hold` is a demand_type on the wire, not a thing a
    // storekeeper says (§2.6, D-123 ruling 9).
    quoteHoldStatement:
      "Holds stock for a customer who asked for it, before there's an order. It's released if no order follows.",
    demandRef: "What is it for?",
    demandRefPlaceholder: "BuildCo · 200 m of 70 mm² · promised Thursday",
    demandRefHint:
      "The customer and what they asked for. Use the same wording again to add stock to this hold.",
    demandRefTooShort:
      "Write at least {{min}} characters. A one- or two-letter note gets confused with somebody else's hold the same day.",
    quantity: "Quantity ({{uom}})",
    allowBackorder: "Allow the hold even if it exceeds available stock",
    submit: "Place hold",
    saving: "Placing hold…",
    failed: "Could not place the hold.",
    created: "Held {{quantity}} {{uom}}.",
    expiresAt: "Released automatically on {{when}} unless it becomes an order.",
    available: "Available now: {{quantity}} {{uom}}.",
    heldFor: "It is already held for: {{ref}}.",
    // Multi-piece holds (the §4.1 off-cut combo action): one hold PER PIECE, so
    // a partial result must never read as a clean one.
    allCreated: "All {{n}} pieces are on hold.",
    partial: "{{ok}} of {{n}} pieces were held — the rest failed. See each piece below.",
    multiHint: "One hold per piece: {{n}} pieces, {{total}} {{uom}} in total.",
    kind: {
      sales_order_line: "Sales order line",
      work_order_component: "Work order component",
      quote_hold: "Quote hold",
      maintenance_order_part: "Maintenance order part",
      transfer_line: "Transfer line",
    },
    // The kinds this panel does NOT place, and what does place them (D-118:
    // absent with the reason named, never a control that cannot succeed).
    otherKindsHead: "Other kinds of hold are not placed here:",
    otherKinds: {
      work_order_component:
        "Production places these itself. Releasing a work order holds every material on its bill of materials automatically.",
      sales_order_line:
        "Stock for a sales order is held by the order itself, in Sales. Use the form above to hold stock for a customer.",
      maintenance_order_part:
        "Maintenance parts can't be held here. Hold them for the job above instead.",
    },
  },
  cut: {
    title: "Cut",
    sourceRemaining: "Remaining on this item: {{remaining}} {{uom}}",
    colLength: "Length ({{uom}})",
    colKind: "Piece",
    colSerial: "Serial",
    serialAuto: "Auto",
    kindChild: "Keep",
    kindWaste: "Waste",
    addPiece: "Add a piece",
    kerf: "Blade width per cut ({{uom}})",
    kerfHint: "Material lost at each separation; charged once per cut.",
    qcFail: "This is a quality-failure cut (the pieces are quarantined)",
    submit: "Cut",
    saving: "Cutting…",
    failed: "Could not cut this item.",
    freshRemaining: "It now has {{remaining}} {{uom}} left — re-enter the cut.",
    done: "Cut into {{n}} pieces. {{remaining}} {{uom}} left on the source.",
    offcut: "off-cut",
    waste: "Waste: {{total}} {{uom}}",
    reservationNotCovered: "The existing hold could not be moved onto a single piece — review it.",
  },
  remeasure: {
    // §4.12: a container's quantity is corrected here, not by a stock adjustment.
    containerHint:
      "This corrects what this container holds. Loose stock is corrected with a stock adjustment.",
    title: "Re-measure",
    current: "Recorded now: {{remaining}} {{uom}}",
    entered: "Measured value ({{uom}})",
    submit: "Save measurement",
    saving: "Saving…",
    failed: "Could not save the measurement.",
    adjusted: "Corrected by {{delta}} {{uom}}.",
    withinTolerance: "Within tolerance — nothing was adjusted.",
  },
  move: {
    title: "Move",
    moving: "Moving {{quantity}} {{uom}}",
    destination: "Move to",
    noOrigin: "This item is not stamped to a location yet — scan it first.",
    submit: "Move",
    saving: "Moving…",
    failed: "Could not move this item.",
    done: "Moved.",
    openApprovals: "Open approvals",
    heldForApproval: "This move is above the approval threshold and is waiting for a decision.",
  },
  quarantine: {
    title: "Quarantine",
    reason: "Why is this being held?",
    relocate: "Also move it to the quarantine area",
    relocateHint: "Leave unticked to hold it where it stands.",
    submit: "Quarantine",
    failed: "Could not quarantine this item.",
    done: "Quarantined.",
  },
  releaseUnit: {
    title: "Release from quarantine",
    note: "Note",
    returnTo: "Return to",
    inPlace: "Leave where it is",
    submit: "Release",
    failed: "Could not release this item.",
    done: "Released.",
  },
  retire: {
    title: "Retire",
    hint: "Only an empty or fully drained container can be retired. This writes a permanent ledger entry.",
    reasonCode: "Reason",
    confirm: "Yes, retire {{serial}}",
    submit: "Retire",
    failed: "Could not retire this item.",
    done: "Retired.",
  },

  // =========================================================================
  // FLOOR PWA (spec 06 §4.1/§4.2 floor, §4.4 Transfer Receive, §4.5 Count
  // Worksheet, §9.2 the offline outbox — D-048).
  //
  // These keys live in the MODULE's namespace, not the office AREA's, and that
  // is deliberate: `inventory.*` is module 06's dictionary (this file's header
  // says so), and the floor screens are module 06's screens. Starting a second
  // `floorInventory.*` namespace next to the floor code would fork the very
  // thing the office/floor split must share — the vocabulary a KazKabel
  // storekeeper and the operator on the same shift use for the same bobbin.
  //
  // COPY RULES specific to the floor, applied to every string below:
  //  · short enough to read at arm's length, in a sentence a shift lead would
  //    actually say ("Bobbin changed — remaining is now 1 543 m");
  //  · no engineering vocabulary: no "409", no "idempotency", no "outbox" —
  //    the operator's model is "waiting to send" / "needs you";
  //  · every failure sentence says what to DO next, because the alternative on
  //    a factory floor is that the device gets put down.
  // =========================================================================
  // §4.12 Stock Adjustment (D-315) ---------------------------------------
  // The office door onto §5 row 25's `adjustment` and row 48's explicit hold.
  // Entity nouns come from useTerm (lot, location, facility, work order).
  adjust: {
    title: "Adjust stock",
    intro: "Book in or write off loose stock; containers use their own card.",
    entry: "Adjust stock",
    entryRow: "Adjust",
    fromProduction: "Record a stock adjustment",
    back: "Back to {{place}}",
    workOrderClosed:
      "For {{name}}. It is closed and takes no material back, so leftover material comes back onto the books here.",
    workOrderOpen: "For {{name}}.",
    recordAgainst: "Record it against {{name}}",
    recordAgainstHint:
      "The order then lists it under Adjustments recorded against this order. Untick it if the material is not this order's.",
    directionLabel: "Direction",
    increase: "Add to stock",
    decrease: "Take off stock",
    product: "Product",
    productPlaceholder: "Search by SKU or name",
    noLot: "None — this product is not tracked by batch",
    pickLot: "Choose one",
    lotRequired: "This product is tracked, so choose which {{lot}} this stock belongs to.",
    lotNone: "This product has no {{lot}} yet. One is created when stock is received.",
    location: "Where the stock is",
    counterLabel: "Booked against",
    counter: {
      found: "Not on the books (found)",
      wip: "Work in progress ({{path}})",
      scrap: "Written off",
    },
    counterShort: {
      found: "not on the books (found)",
      wip: "work in progress",
      scrap: "written off",
    },
    counterMissing: "This plant has no location to book this against. Ask an administrator.",
    quantity: "Quantity",
    unit: "Unit",
    unitBase: "{{code}} (base)",
    quantityInvalid: "Enter a quantity above zero, with at most 6 decimals.",
    reason: "Why",
    reasonPick: "Choose a reason",
    note: "What happened, in your words",
    noteShort: "Write at least 10 characters. {{n}} so far.",
    noteCount: "{{n}} of 2000",
    askApproval: "Send it for approval even if it is small",
    approvalHint:
      "Above your company's approval limit the adjustment waits for a manager instead of posting. You will see which happened.",
    loose: "Loose stock here: {{qty}} {{uom}}",
    looseNone: "Nothing lies loose here to take off.",
    pinned: "Containers here also hold this stock ({{n}}). Correct those on their own cards.",
    pinnedOpen: "Open the first",
    wipHeld: "Work in progress holds {{qty}} {{uom}} of this stock.",
    overLoose: "That is more than the {{qty}} {{uom}} lying loose here.",
    overWip:
      "Work in progress holds only {{qty}} {{uom}} of this stock, so no more can come back from it.",
    otherUnit:
      "The limits above are in the base unit. In another unit, the quantity is checked when you save.",
    waitStock: "Waiting for the stock figures to load.",
    incomplete: "Fill in every field to record the adjustment.",
    submit: "Record adjustment",
    saving: "Recording…",
    failed: "Could not record the adjustment.",
    posted: "Posted to the ledger.",
    held: "Waiting for approval. Nothing has changed on the books yet.",
    seeLedger: "See it in the ledger",
    openRequest: "Open the request",
    another: "Record another",
    loading: "Loading the adjustment form…",
    errorTitle: "Could not load what the form needs",
    noPlantTitle: "You have no plant to adjust stock in",
    noPlantBody:
      "Stock adjustments are recorded in a plant you work in. Ask an administrator to add you to one.",
    forbiddenTitle: "You cannot adjust stock",
    forbidden: "Only storekeepers and administrators record stock adjustments.",
    prefillMissing:
      "Something the link named no longer exists or cannot be used here, so that field was cleared.",
    offlineTitle: "You are offline",
    offlineBody: "Stock figures cannot be read without a connection. Nothing was sent.",
    // The #282 fix round.
    looseUnknown:
      "This place holds more stock rows than one page, so the loose stock here is not shown. The quantity is checked when you save.",
    wipOutput:
      "Work in progress holds {{qty}} {{uom}}, below zero because a work order took it out as its output. An increase isn't limited.",
    fence: {
      quarantine_place:
        "Stock in quarantine cannot go into work in progress. Book it against scrap, or let quality release it first.",
      lot_not_released:
        "Quality has not released this {{lot}}, so it cannot go into work in progress. Book it against scrap, or release it first.",
    },
    transitHold:
      "A load lost in transit is written off only with a manager's approval. It will wait for a decision whatever its value.",
    container: {
      live: "{{serial}} still holds stock, so what came off it goes back onto it with Re-measure on its own card, not as loose stock here.",
      held: "{{serial}} is held by quality. Its leftover waits for quality's decision before it goes anywhere.",
      no_stock:
        "{{serial}} holds no stock any more, so its leftover comes back as loose stock at its {{lot}}.",
    },
    containerOpen: "Open {{serial}}",
    looseConfirm: "This is loose material, not material on a container",
    looseReason:
      "Material still on a container is corrected on that container's card with Re-measure.",
    expectedOrder:
      "The order still counts {{qty}} {{uom}} of this as issued and not returned. Enter what you weighed.",
    expectedFloor: "The floor recorded {{qty}} {{uom}} coming back. Enter what you weighed.",
    noteLeftoverClosed: "Leftover material from {{name}}, carried back after it closed.",
    noteLeftover: "Leftover material from {{name}}.",
    noteReceipt: "Correction to what {{name}} receipted.",
    unknown:
      "The last press may have gone through. Check before you change anything: Record sends the same adjustment again and cannot post it twice.",
    unknownLedger: "Check the ledger",
    unknownApprovals: "Check the approvals",
    startOver: "Start over",
  },

  // §4.12's pending document at /inventory/approvals/{id} (D-315).
  adjustRequest: {
    title: "Adjustment request",
    loading: "Loading the request…",
    errorTitle: "Could not load this request",
    notFoundTitle: "This request does not exist or is in a plant you cannot see",
    notFoundBody: "Check the link, or open the approvals list.",
    lotPart: ", {{lot}} {{code}}",
    summary: {
      increase:
        "Add {{qty}} {{uom}} of {{product}}{{lotPart}} at {{place}}, booked against {{counter}}",
      decrease:
        "Take {{qty}} {{uom}} of {{product}}{{lotPart}} off stock at {{place}}, booked against {{counter}}",
      relocation: "Move {{qty}} {{uom}} of {{product}}{{lotPart}} from {{from}} to {{to}}",
      other: "{{type}}: {{qty}} {{uom}} of {{product}}{{lotPart}}, from {{from}} to {{to}}",
    },
    reason: "Why",
    note: "In their words",
    requested: "Requested by {{who}} on {{when}}",
    status: {
      pending_approval: "Waiting for a decision since {{when}}.",
      posted: "Approved by {{who}} on {{when}} and posted.",
      rejected: "Rejected by {{who}}: {{reason}}",
      stale:
        "Rejected because the stock changed before the decision. Record it again against today's stock.",
      cancelled: "Withdrawn by {{who}} on {{when}}.",
    },
    seeLedger: "See the movement in the ledger",
    workOrder: "Recorded against",
    recordAgain: "Record it again",
    value: "Estimated value",
    stateChanged: "Someone decided or withdrew this request a moment ago. It now reads as below.",
  },

  // Module 04's S2 section (P3-H-3, D-342 ruling 2, D-391 ruling 3): the
  // movements and requests recorded against one work order.
  woAdjustments: {
    title: "Adjustments recorded against this order",
    loading: "Loading the adjustments recorded against this order…",
    empty: "No adjustments recorded against this order",
    error: "The adjustments recorded against this order could not be loaded.",
    requestId: "Request ID",
    retry: "Try again",
    more: "Only the latest {{count}} of each are shown. The ledger has the rest.",
    inLedger: "In the ledger",
    openRequest: "Open the request",
  },

  floor: {
    back: "Back",

    sync: {
      online: "Online",
      offline: "No signal",
      pending: "{{count}} waiting to send",
      allSynced: "Everything sent",
      attention: "{{count}} need you",
      retrying: "Trying again — attempt {{attempts}}. Your work is saved.",
      signedOut:
        "You were signed out. Sign in again to finish sending your work — nothing is lost.",
      notDurable:
        "This device cannot save your queue. Work sent while you have no signal will be lost if the app closes.",
      // `navigator.onLine` only means "on a network". This is the plant AP with
      // no backhaul: associated, green by the browser's reckoning, unreachable.
      unreachable: "Can't reach the system",
      sendNow: "Send now",
      otherUser: "{{count}} item(s) were saved by someone else. Sign in as them to send them.",
      hydrateFailed:
        "This device could not read the work saved on it. Do not sign out — try again in a moment.",
      notSavedTitle: "That was NOT saved",
      notSavedBody:
        "This device could not store it, so nothing is waiting to send. Do it again, and free up space if it keeps failing.",
    },

    cache: {
      showingSnapshot: "Showing what this device last loaded",
      ageJustNow: "just now",
      ageMinutes: "{{count}} min ago",
      ageHours: "{{count}} h ago",
      ageDays: "{{count}} d ago",
      needConnectionTitle: "Not loaded on this device yet",
      needConnectionBody: "Find signal once to load {{what}}. After that it works with no signal.",
      checking: "checking for updates",
    },

    // D-119: the operator, named on every floor screen. `who` is the identity
    // the last sign-in on this device used — the same stamp the outbox judges
    // a queued tap against, so the two can never disagree.
    principal: {
      signedIn: "Signed in: {{who}}",
      signInAgain: "Sign in again so your work is booked to you.",
    },

    home: {
      title: "Floor",
      scan: "Scan",
      scanSub: "Bin, then bobbin — updates where it is",
      stock: "Find stock",
      stockSub: "Search by product and length",
      receive: "Receive a transfer",
      receiveSub: "Check off what arrived",
      counts: "Stock count",
      countsSub: "Count a bin",
      tray: "Needs you",
      traySub: "Nothing is stuck",
      productCard: "Product card",
      productCardSub: "Scan a product label for its steps",
      // D-118: a tile the permission mirror has no answer for stays on the hub
      // and says so. Deleting it would tell the operator they are not allowed,
      // which is a different (and untrue) statement.
      //
      // TASK #115 SPLIT "WE CANNOT TELL YET" IN TWO, because the two halves ask
      // different things of the operator. The line above is the feed that has
      // STOPPED RETRYING — it needs signal, and the tile now offers the retry.
      // The three below are the window a fast thumb actually taps into, and the
      // answers that window owes a tap (floor-home.tsx's header block).
      unavailableUnknown: "Needs signal once before this tablet can open it",
      unavailableChecking: "Checking what you can do here",
      unavailableWait: "Still checking. It opens by itself.",
      unavailableRetryHint: "Tap to check again when you have signal",
      unavailableRetrying: "Checking again…",
    },

    op: {
      scan: "Scan · {{payload}}",
      cut: "Cut · {{pieces}} piece(s)",
      remeasure: "Re-measure · {{measure}}",
      countRecord: "Count · {{value}}",
      countFound: "Count · found item",
      countSubmit: "Count · finish",
      transferReceive: "Receive · {{lines}} line(s)",
      // Module 04 production events on the SAME queue (D-048/D-054, D-080):
      // the label namespace follows the engine that renders it.
      opStarted: "Operation · start",
      opPaused: "Operation · pause",
      opResumed: "Operation · resume",
      opCompleted: "Operation · complete",
      qtyReported: "Output · {{good}} good, {{scrap}} scrap",
      materialIssued: "Material · {{qty}} {{uom}}",
      // D-081: a negative material_issued is the RETURN — the tray names it
      // instead of rendering a minus sign.
      materialReturned: "Material return · {{qty}} {{uom}}",
      // The 04-C kinds (task #96 — these five rode the raw-name fallback below
      // long after their builders shipped; the label table is now exhaustive
      // over the §5.1 union, floor-ops.ts).
      downtimeStarted: "Downtime · start",
      downtimeEnded: "Downtime · end",
      calibrationRecorded: "Calibration · claimed {{claimed}}, measured {{measured}} {{uom}}",
      // D-085's shared direction sentences, in the tray's one-line grammar.
      serialProduced: "Serial · came off the machine, {{qty}} {{uom}}",
      serialConsumed: "Serial · went into the machine, {{qty}} {{uom}}",
      defectFlagged: "Defect · {{qty}} flagged",
      // The visible-raw fallback for a kind THIS build does not know (older or
      // newer bytes on the device) — degrades by naming itself, never blank.
      productionEvent: "Floor action · {{type}}",
      // Module 05 waste captures on the SAME queue (slice 05-B): the tray
      // names the physical fact, because by tray time the station context is
      // gone (floor-ops.ts opTarget).
      wasteCapture: "Waste · {{qty}} {{uom}}",
      remnantRegister: "Remnant · {{length}} m",
      // Module 07 inspection taps on the SAME queue (slice 07-A). The value is
      // the measurement where the item has one, else the verdict; the
      // inspection number rides `OutboxItem.identity` beside it.
      qualityResult: "Check · {{value}}",
      qualityComplete: "Inspection · finish",
    },

    scan: {
      title: "Scan",
      noPermission: "Your role cannot record scans. Ask a storekeeper.",
      actionLegend: "What are you doing?",
      action: {
        locate: "Putting it away",
        lookup: "Just looking",
        count: "Counting",
        receive: "Receiving",
      },
      inputLabel: "Scan or type the label",
      inputPlaceholder: "Point the scanner, or type the serial",
      go: "Go",
      cameraStart: "Use the camera",
      cameraStop: "Stop the camera",
      cameraUnavailable: "This device has no camera scanner. Use the scan gun or type the serial.",
      cameraDenied: "The camera could not start. Use the scan gun or type the serial.",
      measureLabel: "Measured length (optional)",
      measurePlaceholder: "e.g. 1543",
      measureHint:
        "Leave blank unless you measured it. A big difference is corrected on the ledger.",
      measureInvalid: "Enter a number, up to six decimals.",
      deviceUnstable:
        "This device could not save its id, so scanning a bin then a bobbin may not link them after a restart.",
      noStation:
        "This device has no station. Scan a bin and tap \u201cUse this as this device\u2019s spot\u201d.",
      stationBound: "This device is tied to a spot.",
      stationBind: "Use this as this device\u2019s spot",
      stationUnbind: "Untie",
      doneHere: "Done here",
      sessionWaiting: "Waiting to send",
      sessionNeedsYou: "Needs you",
      sending: "Sending…",
      queuedTitle: "Saved on this device",
      queuedOfflineBody: "It will be sent, and the label resolved, as soon as you have signal.",
      queuedSlowBody: "The network is slow. It will finish in the background.",
      failedTitle: "That scan was not accepted",
      clear: "Clear",
      unknownTitle: "Label not recognised",
      // §4.2's "register it?" path is NOT built on the floor (see §4.2's
      // deferred list): creating a stock unit needs a serial, a kind, a product
      // and a location, which is the office receipt screen's form. Saying where
      // it happens is honest; offering a button that does not exist is not.
      unknownBody: "Saved, but this label isn't one of ours. The office registers new items.",
      locationBody: "Bin recorded. Now scan the item — it will be placed here.",
      unitTitleFallback: "Item",
      movedBody: "Now in {{path}}.",
      notMovedBody: "Recorded. Its place was not changed.",
      offsiteBody:
        "This is at {{here}}, a different plant from the records. Nothing was moved; the office has been told.",
      remeasureAdjusted: "Length corrected by {{delta}} — now {{remaining}}.",
      remeasureWithinTolerance: "Length updated to {{remaining}} (within tolerance).",
      openUnit: "Open this item",
    },

    stock: {
      title: "Find stock",
      searchLabel: "What are you looking for?",
      searchPlaceholder: "e.g. 70mm 200m",
      pressGo: "Press Go to search.",
      offlineBody: "Searching needs signal, because stock changes by the minute.",
      promptTitle: "Search for stock",
      promptBody: "Type a product and a length, or scan a label.",
      errorTitle: "Could not search",
      noneTitle: "Nothing matches “{{query}}”",
      noneBody: "Try without the length.",
      noneBroadBody: "Nothing matches even without the length.",
      showAnyway: "Search without the length",
      broadenedNote: "Showing results without the length you asked for.",
      groupUnits: "Items",
      groupCombos: "Off-cut pairs",
      groupBulk: "Bulk",
      groupCapped: "Not all matches are shown — narrow the search.",
      productsCapped: "Too many products match — some stock is not shown. Add the product name.",
      comboTotal: "Together: {{total}}",
      lastSeen: "Last seen {{hours}} h ago",
      neverScanned: "Never scanned",
      reserved: "Held · {{ref}}",
      open: "Open",
      degraded: "No off-cut price hint is shown.",
    },

    unit: {
      title: "Item",
      thisUnit: "this item",
      // The accessible name of the cut/re-measure mode pair (F6). Two
      // `aria-pressed` buttons in a group with no name announce as two loose
      // buttons; the group name is what makes them read as one choice.
      modeLabel: "What to do with this item",
      notFoundTitle: "Not found",
      notFoundBody: "This item does not exist, or it is not yours to see.",
      ofCapacity: "of {{capacity}} {{uom}}",
      noCapacity: "No stated capacity",
      offcut: "Off-cut",
      noCutPermission: "Your role cannot cut. Ask a storekeeper.",
    },

    cut: {
      title: "Cut",
      pieceN: "Piece {{n}} length",
      piecePlaceholder: "e.g. 200",
      pieceKind: "Piece type",
      kindChild: "Keep as a new piece",
      kindWaste: "Waste (trim)",
      addPiece: "Add another piece",
      kerf: "Blade width ({{uom}}, optional)",
      kerfHint: "Charged once per cut through the cable, not per piece.",
      summary: "Planned {{planned}} of {{remaining}} {{uom}}",
      overRemaining: "That is more than this item has. It may still be allowed within tolerance.",
      malformed: "Every length must be a number, up to six decimals.",
      noPieces: "Add at least one piece.",
      staleBaseline: "This length is what the device last loaded. Check the bobbin before you cut.",
      replan: "Bobbin changed — remaining is now {{remaining}} {{uom}}. Re-enter the cut.",
      submit: "Cut",
      // The plan rides the button, because the button is fixed to the bottom of
      // the screen and the summary above it may be scrolled away at the moment
      // of the tap.
      submitWithPlan: "Cut {{pieces}} piece(s) · {{planned}} of {{remaining}} {{uom}}",
      // A cut is not idempotent, so a committed plan disarms the form and
      // re-arming is deliberate — a bouncing finger must not cut twice.
      planAnother: "Plan another cut",
      submitting: "Cutting…",
      queued: "Saved on this device. It will be cut on the system as soon as you have signal.",
      failedTitle: "That cut was not accepted",
      doneTitle: "Cut done",
      parentNow: "This item now has {{remaining}} {{uom}}.",
      reservationNotCovered:
        "A hold on this item could not follow onto any single new piece — a storekeeper needs to sort it out.",
    },

    remeasure: {
      title: "Re-measure",
      noPermission: "You cannot re-measure items.",
      label: "Measured length ({{uom}})",
      submit: "Save the measurement",
      queued: "Saved on this device. It will be sent as soon as you have signal.",
      failedTitle: "That measurement was not accepted",
      adjusted: "Corrected by {{delta}} — now {{remaining}}.",
      withinTolerance: "Updated to {{remaining}} (within tolerance, no ledger change).",
    },

    counts: {
      title: "Stock counts",
      emptyTitle: "Nothing to count",
      emptyBody: "No count has been started for you yet. A storekeeper opens one.",
      emptyOfflineBody: "No count is saved on this device. Find signal once to load your counts.",
      // A failed read must never render as "nothing to count" (§4 preamble:
      // every list carries an error state with retry + support code). "No count
      // has been started for you" is a claim this device cannot make when the
      // request did not come back.
      thisList: "your counts",
      loadFailedTitle: "Could not load your counts",
      loadFailedBody: "This is not the same as having none. Try again.",
      blind: "Blind count",
      open: "Count this",
    },

    count: {
      title: "Count",
      thisWorksheet: "this count",
      loadFailedTitle: "Could not load this count",
      loadFailedBody: "Try again, or ask a storekeeper whether it is still open.",
      blindBadge: "Blind count",
      blindNote: "Count what you see. The expected numbers are not shown, on purpose.",
      openNote: "Enter what you count in each bin.",
      progress: "{{counted}} of {{total}} counted",
      notCounting: "This count is {{status}} and is no longer taking entries.",
      noPermission: "Your role cannot record counts. Ask a storekeeper.",
      binHead: "Bins",
      emptyTitle: "This count has no lines",
      emptyBody: "Nothing was in scope when it was started.",
      measureLabel: "Measured ({{uom}})",
      qtyLabel: "Counted ({{uom}})",
      noteLabel: "Note",
      notePlaceholder: "Optional",
      skipHint: "A note with no number marks the line skipped.",
      record: "Save this line",
      recorded: "Save again",
      recordSaving: "Saving\u2026",
      saved: "Saved on this device.",
      lineParked: "This line was refused and is waiting for you.",
      openTray: "Open what needs you",
      binScanLabel: "Scan the bin you are in",
      binScanPlaceholder: "Point the scanner at the rack label",
      binNoMatch: "\u201c{{payload}}\u201d is not a bin on this count. Pick it from the list.",
      handOff:
        "Counting is done. A storekeeper closes the count \u2014 hand the device over or tell them.",
      foundHead: "Found something not on the list",
      foundHint: "Scan it and it is added to this count.",
      foundNeedsBin: "Pick the bin you are in first.",
      foundLabel: "Scan the extra item",
      foundPlaceholder: "Point the scanner at it",
      foundQueued: "Saved. It is added to the count once the label is recognised.",
      foundNotSaved: "That was not saved on this device. Try again.",
      submit: "Finish the count",
      submitBlocked: "{{remaining}} line(s) still to count",
      submitQueued: "Saved on this device. The count is closed as soon as you have signal.",
      submitFailedTitle: "The count could not be closed",
      submitted: "Counted and sent for review.",
    },

    receive: {
      listTitle: "Receive a transfer",
      staysInTransit: "Not receiving this one \u2014 it stays in transit.",
      progress: "{{done}} of {{total}} lines done",
      scanTicked: "\u2713 {{name}} ticked \u00b7 {{done}} of {{total}}",
      blockedReason: "Every short line needs a reason",
      blockedOver: "One line is more than was sent",
      blockedMalformed: "Check the numbers",
      title: "Receive",
      thisManifest: "this transfer",
      emptyTitle: "Nothing on its way",
      emptyBody: "No transfer has been dispatched to you.",
      emptyOfflineBody: "No transfer is saved on this device. Find signal once to load them.",
      // The INBOUND LIST's own failure state (the detail screen's is below).
      // "No transfer has been dispatched to you" is an affirmative claim, and a
      // receiving bay that walks away on a 500 leaves stock on the dock.
      thisInbound: "what is on its way to you",
      listLoadFailedTitle: "Could not load what is on its way",
      listLoadFailedBody: "This is not the same as nothing arriving. Try again.",
      noCarrier: "No carrier named",
      open: "Open",
      loadFailedTitle: "Could not load this transfer",
      loadFailedBody: "Try again, or check the paper manifest.",
      noPermission: "Your role cannot receive transfers. Ask a storekeeper.",
      wrongState: "This transfer is {{status}} and is not taking receipts.",
      noLinesTitle: "Nothing on this transfer",
      noLinesBody: "It was dispatched with no lines. Check with the sending plant.",
      unnamedLine: "Line {{n}}",
      dispatched: "Sent: {{qty}} {{uom}}",
      tickWhole: "Arrived, in full",
      qtyLabel: "Arrived ({{uom}})",
      overReceive:
        "That is more than was sent. Receive what was sent; extra found stock is a separate correction.",
      reasonLabel: "Why is it short?",
      reasonPick: "Pick a reason…",
      reason: {
        spillage: "Spillage",
        damage: "Damaged in transit",
        short_shipped: "Short-shipped",
        miscount: "Miscount at dispatch",
        other: "Other",
      },
      shortfallNote: "The missing amount stays in transit until it is found or written off.",
      needsReason: "Every short line needs a reason before you can confirm.",
      scanHead: "Tick items by scanning",
      scanLabel: "Scan an item on this transfer",
      scanPlaceholder: "Point the scanner at it",
      scanNoMatch: "“{{payload}}” is not on this transfer.",
      confirm: "Confirm {{count}} line(s)",
      queued: "Saved on this device. It will be received as soon as you have signal.",
      failedTitle: "That receipt was not accepted",
      doneComplete: "Received in full.",
      donePartial: "Received. Some lines are still outstanding.",
    },

    tray: {
      title: "Needs you",
      pendingNote: "Still waiting to send: {{count}}.",
      // THE QUEUE, NAMED (D-121). The count alone is what an operator
      // re-taps against; the list is what stops them.
      pendingHead: "Waiting to send",
      pendingBody:
        "Saved on this device. They'll send when you have signal, and you don't need to redo them.",
      pendingBlocked: "This one is at the front of the queue and is being retried.",
      emptyTitle: "Nothing is stuck",
      emptyBody: "Everything you have done has been sent.",
      emptyBodyPending: "Nothing is stuck — the rest is still on its way.",
      unknownReason: "This could not be sent and there is no reason recorded.",
      networkFailure: "This device could not reach the system.",
      forbidden: "Your role is not allowed to do this.",
      notFound: "What this refers to no longer exists.",
      freshRemaining: "The item {{serial}} now has {{remaining}} left.",
      availableQty: "Only {{available}} is actually available.",
      attempts: "Tried {{count}} time(s)",
      adviceFutile: "Trying again will not help — this was already used for something else.",
      adviceReplan:
        "Things changed while this was waiting. Open it and do it again with what is there now.",
      retry: "Try again",
      open: "Open it",
      discard: "Throw it away",
      discardConfirm: "Yes, throw it away",
      discardWarning:
        "This removes what is waiting on this device. It does not undo anything the system already did.",
      supportDetails: "Details for support",
      otherUserHead: "Saved by someone else",
      otherUserBody:
        "{{count}} item(s) were saved by another person on this device. They are not sent under your name. Ask them to sign in.",
      // The D-080 review section: server-conflicted production events. The
      // copy must say BOTH halves — nothing is lost (the office has it), and
      // nothing here will be re-sent (dismiss clears only this device's note).
      reviewHead: "Needs office review",
      reviewBody:
        "The office got these but couldn't apply them. A supervisor will sort them out; nothing is lost.",
      reviewDismiss: "Clear from this device",
      reviewDismissConfirm: "Yes, clear it",
      reviewDismissWarning:
        "This only clears the note on this device. The record is already with the office, where a supervisor will resolve it.",
      reviewReasonFallback: "Could not be applied ({{reason}}).",
      reviewReasons: {
        invalid_transition: "The operation was already in a different state when this arrived.",
        qty_exceeds_remaining: "The reported quantity is more than this operation had left.",
        op_already_completed: "The operation was already completed.",
        wo_cancelled: "The work order was cancelled.",
        stock_insufficient: "There was not enough recorded stock for this material.",
        unknown_reference: "What this refers to could not be found.",
        stale_snapshot:
          "The time on this device looks wrong. The office will check when this really happened.",
        qc_hold: "The operation is on a quality hold.",
        worker_not_authenticated:
          "This was recorded under a different worker than the one signed in.",
        unsupported_event_kind: "The system does not support this action yet.",
        result_missing: "The system's answer did not mention this action.",
        result_unrecognized: "The system's answer for this action could not be understood.",
      },
    },
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
// `typeof inventoryEn` alone would carry string-LITERAL types out of the
// `as const`, and no real translation can satisfy those.
export type InventoryDict = Translated<typeof inventoryEn>;

// ru is TRANSLATED (see inventory.ru.ts). zh/fr are still English placeholders;
// that claim is declared in src/i18n-completeness.ts and enforced by
// tools/ci/i18n-parity.ts, which fails if `ru` ever regresses to en values.
export const inventoryRu: InventoryDict = inventoryRuDict;
